<template>
  <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 py-8">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-20">
      <Loader />
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center"
    >
      <p class="text-destructive mb-4">{{ error }}</p>
      <button
        @click="fetchSubscriptions"
        class="px-6 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors"
      >
        Retry
      </button>
    </div>

    <!-- Main Content Area -->
    <div
      v-else
      class="grid grid-cols-1 lg:grid-cols-3 xl:gap-8 gap-4 items-stretch"
    >
      <div
        v-for="pricingPlan in pricingPlans"
        :key="pricingPlan.id"
        class="bg-card rounded-2xl p-4 lg:p-8 border border-border flex flex-col h-full hover:shadow-lg transition-shadow duration-300"
      >
        <div v-if="pricingPlan" class="flex flex-col h-full">
          <div class="flex-1">
            <div class="mb-8">
              <h3 class="text-foreground font-bold text-2xl mb-2">
                {{ pricingPlan.name }}
              </h3>
              <p class="text-muted-foreground text-[15px] leading-relaxed">
                {{ pricingPlan.description }}
              </p>
            </div>
            <div class="flex flex-col items-center mb-12 w-full">
              <Tabs
                v-model="planBillingCycles[pricingPlan.name]"
                class="w-full max-w-[400px]"
              >
                <TabsList class="grid w-full grid-cols-2">
                  <TabsTrigger value="Monthly">Monthly Billing</TabsTrigger>
                  <TabsTrigger value="Yearly">Annual Billing</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div class="mb-8">
              <div class="flex items-baseline justify-center gap-1">
                <span
                  v-if="
                    planBillingCycles[pricingPlan.name] === 'Yearly' &&
                    pricingPlan.id !== 1
                  "
                  class="text-xl text-muted-foreground line-through mr-2"
                >
                  {{ getCurrencySymbol(pricingPlan.currency)
                  }}{{ getMonthlyPrice(pricingPlan) * 12 }}
                </span>
                <span class="text-4xl font-bold text-foreground">
                  {{ getCurrencySymbol(pricingPlan.currency)
                  }}{{ currentPrice(pricingPlan) }}
                </span>
                <span class="text-muted-foreground text-lg">
                  /
                  {{
                    planBillingCycles[pricingPlan.name] === "Monthly"
                      ? "month"
                      : "year"
                  }}
                </span>
              </div>
              <p
                v-if="
                  planBillingCycles[pricingPlan.name] === 'Yearly' &&
                  currentPrice(pricingPlan) > 0
                "
                class="text-sm text-muted-foreground mt-1 text-center"
              >
                ({{ getCurrencySymbol(pricingPlan.currency)
                }}{{ (pricingPlan.price / 12).toFixed(1) }}
                / month)
              </p>
            </div>

            <Button
              @click="handleCheckout(pricingPlan)"
              class="w-full mb-8 py-6 text-lg font-semibold bg-primary text-primary-foreground dark:text-foreground dark:bg-background hover:bg-primary/90 dark:hover:bg-background/90 transition-colors"
              variant="default"
              :disabled="
                (paymentLoading && selectedPlan?.id === pricingPlan.id) ||
                isCurrentPlan(pricingPlan)
              "
            >
              <span
                v-if="paymentLoading && selectedPlan?.id === pricingPlan.id"
                class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"
              ></span>
              {{
                paymentLoading && selectedPlan?.id === pricingPlan.id
                  ? "Processing..."
                  : getButtonText(pricingPlan)
              }}
            </Button>

            <div class="space-y-4">
              <div
                v-for="feature in pricingPlan.features"
                :key="feature"
                class="flex items-start gap-3"
              >
                <Check class="w-5 h-5 text-muted-foreground mt-0.5" />
                <p class="text-[15px] text-foreground">{{ feature }}</p>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="bg-card rounded-lg border border-border p-12 text-center"
        >
          <svg
            class="w-24 h-24 mx-auto text-muted-foreground mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 class="text-xl font-semibold text-foreground mb-2">
            No Hub Selected
          </h3>
          <p class="text-muted-foreground mb-6">
            Select a hub from the sidebar or create a new one
          </p>
          <button
            @click="showCreateHubModal = true"
            class="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Create New Hub
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="paymentStage"
      class="fixed inset-0 bg-overlay/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
    >
      <div
        class="relative bg-card w-full max-w-[550px] m-auto flex-col flex rounded-2xl shadow-2xl border border-border p-8 animate-in fade-in zoom-in duration-200"
      >
        <div class="flex items-center justify-between mb-8">
          <h2 class="text-xl text-foreground font-bold italic tracking-tight">
            Complete Payment
          </h2>
          <button
            @click="closePayment"
            class="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-full hover:bg-muted"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="flex flex-col items-center space-y-8">
          <div class="text-center space-y-2">
            <p class="text-[15px] text-muted-foreground">
              You are about to purchase
            </p>
            <p class="text-2xl font-bold text-foreground">
              {{ selectedPlan?.name }}
            </p>
            <p class="text-3xl font-extrabold text-success mt-2">
              {{ getCurrencySymbol(selectedPlan?.currency) }}{{ amount }}
            </p>
          </div>

          <div class="flex gap-4 w-full pt-4">
            <button
              @click="closePayment"
              class="flex-1 py-3.5 px-4 bg-muted border border-border text-foreground rounded-xl hover:bg-muted/80 transition-all font-medium"
            >
              Cancel
            </button>
            <paystack
              v-if="payment_key && reference"
              :buttonClass="'flex-1 py-3.5 px-4 bg-primary text-primary-foreground dark:text-foreground dark:bg-background rounded-xl hover:bg-primary/90 transition-all cursor-pointer text-center font-bold'"
              :buttonText="'Proceed with Payment'"
              :amount="amount * 100"
              :email="email"
              :publicKey="payment_key"
              :reference="reference"
              :onSuccess="processPayment"
              :onCancel="closePayment"
            />
          </div>

          <p class="text-[13px] text-muted-foreground text-center">
            You will be redirected to Paystack to complete your payment
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
import { Button } from "@/components/ui/button";
import { Check } from "lucide-vue-next";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import apiClient from "@/axios";
import paystack from "vue3-paystack/src/vue3-paystack.vue";
import Loader from "../ui/Loader.vue";

export default {
  name: "PricingComponent",
  components: {
    Button,
    Check,
    Tabs,
    TabsList,
    TabsTrigger,
    paystack,
    Loader,
  },
  data() {
    return {
      showCreateHubModal: false,
      selectedHub: null,
      planBillingCycles: {},
      loading: true,
      error: null,
      paymentLoading: false,
      paymentStage: false,
      selectedPlan: null,
      payment_key: null,
      reference: null,
      email: null,
      amount: null,
      userSubscription: null,
      tabs: [
        { id: "projects", name: "Projects" },
        { id: "members", name: "Members" },
        { id: "activity", name: "Activity" },
      ],
      pricingPlans: [
        {
          id: 1,
          name: "Free",
          description:
            "Get started with core tools to set goals, track progress, and explore opportunities.",
          price: 0.0,
          currency: "USD",
          features: [
            "Create up to 2 learning goals",
            "Access to ES Creative Community",
          ],
          actionText: "Get Free",
          billingCycle: "Monthly",
          billingOptions: [
            {
              id: 1,
              name: "Monthly",
              price: 0.0,
              currency: "USD",
            },
            {
              id: 2,
              name: "Yearly",
              price: 0.0,
              currency: "USD",
            },
          ],
        },
        {
          id: 2,
          name: "Premium",
          description: "Learn and explore new opportunities today!",
          price: 10.0,
          currency: "USD",
          features: [
            "80 monthly Edgespace AI credits",
            "Create up to 100 learning goals",
            "Fast and efficient model response",
            "Access to ES Creative Community",
          ],
          actionText: "Get Premium",
          billingCycle: "Monthly",
          billingOptions: [
            {
              id: 1,
              name: "Monthly",
              price: 10.0,
              currency: "USD",
            },
            {
              id: 2,
              name: "Yearly",
              price: 120.0,
              discount: 30,
              currency: "USD",
            },
          ],
        },
        {
          id: 3,
          name: "Premium Pro",
          description:
            "Best for anyone who wants to try out all the available tools",
          price: 30.0,
          currency: "USD",
          features: [
            "200 monthly Edgespace AI credits",
            "Unlimited learning goals",
            "Fast and efficient model response",
            "Access to ES Creative Community",
          ],
          actionText: "Get Premium Pro",
          billingCycle: "Monthly",
          billingOptions: [
            {
              id: 1,
              name: "Monthly",
              price: 30.0,
              currency: "USD",
            },
            {
              id: 2,
              name: "Yearly",
              price: 360.0,
              discount: 30,
              currency: "USD",
            },
          ],
        },
      ],
    };
  },
  methods: {
    selectHub(hub) {
      this.selectedHub = hub;
    },
    formatDate(date) {
      return dayjs(date).fromNow();
    },
    getStatusClass(status) {
      const classes = {
        "In Progress":
          "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
        Planning:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
        Completed:
          "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
      };
      return classes[status] || "bg-muted text-muted-foreground";
    },
    currentPrice(plan) {
      if (!plan.billingOptions) return plan.price;
      const billingCycle = this.planBillingCycles[plan.name] || "Monthly";
      const option = plan.billingOptions.find((o) => o.name === billingCycle);
      return option ? option.price : plan.price;
    },

    getButtonText(plan) {
      // If no user subscription data, show default action text
      if (!this.userSubscription) {
        return plan.actionText;
      }

      // Check if user is currently on this plan
      const currentPlanName = this.userSubscription.subscription_name;

      if (currentPlanName === plan.name) {
        return "Current Plan";
      }

      // Default to the plan's action text
      return plan.actionText;
    },

    isCurrentPlan(plan) {
      if (!this.userSubscription) {
        return false;
      }
      return this.userSubscription.subscription_name === plan.name;
    },

    async handleCheckout(plan) {
      const billingCycle = this.planBillingCycles[plan.name] || "Monthly";
      const currentOption = plan.billingOptions?.find(
        (o) => o.name === billingCycle,
      );
      const paymentProvider =
        currentOption?.payment_provider || plan.payment_provider;

      if (paymentProvider && paymentProvider.toLowerCase() !== "paystack") {
        this.$toast.error(
          "Error",
          `Payment provider '${paymentProvider}' is not available.`,
        );
        return;
      }

      try {
        this.paymentLoading = true;
        this.selectedPlan = plan;

        const subscriptionId = currentOption?.id || plan.id;

        const purchasePayload = {
          subscription_id: subscriptionId,
          auto_renew: false,
        };

        const response = await apiClient.post(
          "/web/subscriptions/purchase",
          purchasePayload,
        );

        if (response.data.status_code === 200 && response.data.data) {
          const data = response.data.data;

          if (data.subscription_name && !data.payment_key) {
            this.$toast.success("Success", response.data.message);
            await this.fetchSubscriptions();
            return;
          }

          this.payment_key = data.payment_key;
          this.email = this.$store.state.user?.email || "";
          this.amount = data.amount;
          this.reference = data.reference;

          this.paymentStage = true;
        } else {
          throw new Error(
            response.data.message || "Failed to initiate purchase",
          );
        }
      } catch (error) {
        console.error("Error initiating purchase:", error);
        const errorMessage =
          error.response?.data?.message ||
          "Failed to initiate subscription purchase. Please try again.";
        this.$toast.error("Error", errorMessage);
      } finally {
        this.paymentLoading = false;
      }
    },
    processPayment() {
      this.$toast.success(
        "Success",
        "Subscription payment completed successfully!",
      );
      this.paymentStage = false;
      this.selectedPlan = null;
      this.fetchSubscriptions();
      this.fetchUserSubscription();
    },
    closePayment() {
      console.log("Payment cancelled");
      this.paymentStage = false;
      this.selectedPlan = null;
    },
    getCurrencySymbol(currencyCode) {
      const symbols = {
        USD: "$",
        EUR: "€",
        GBP: "£",
        NGN: "₦",
      };
      return symbols[currencyCode] || currencyCode;
    },
    async fetchSubscriptions() {
      try {
        this.loading = true;
        this.error = null;
        const response = await apiClient.get(
          "/web/subscriptions/creative-community",
        );

        if (response.data.status_code === 200 && response.data.data) {
          this.pricingPlans = this.mapSubscriptionsToPricingPlans(
            response.data.data,
          );
        } else {
          throw new Error(
            response.data.message || "Failed to fetch subscriptions",
          );
        }
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        this.error =
          error.response?.data?.message ||
          "Failed to load subscription plans. Please try again later.";
        this.$toast.error("Error", this.error);
      } finally {
        this.loading = false;
      }
    },

    async fetchUserSubscription() {
      try {
        const response = await apiClient.get(
          "/web/subscriptions/my-subscription",
        );

        if (response.data && response.data.data) {
          this.userSubscription = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching user subscription:", error);
      }
    },

    mapSubscriptionsToPricingPlans(subscriptions) {
      const groupedPlans = {};

      subscriptions.forEach((sub) => {
        const baseName = sub.name;
        const billingType = sub.duration_days >= 365 ? "Yearly" : "Monthly";

        if (!groupedPlans[baseName]) {
          groupedPlans[baseName] = {
            id: sub.id,
            name: baseName,
            description: sub.description || "",
            price: sub.amount,
            currency: sub.currency,
            features: sub.features || [],
            actionText: `Get ${baseName}`,
            billingCycle: "Monthly",
            billingOptions: [],
          };
        }

        const billingOption = {
          id: sub.id,
          name: billingType,
          price: sub.amount,
          currency: sub.currency,
          discount: billingType === "Yearly" ? 30 : 0,
        };

        groupedPlans[baseName].billingOptions.push(billingOption);
      });

      // Calculate billing cycles with 30% discount
      Object.values(groupedPlans).forEach((plan) => {
        const hasMonthly = plan.billingOptions.some(
          (o) => o.name === "Monthly",
        );
        const hasYearly = plan.billingOptions.some((o) => o.name === "Yearly");

        if (hasMonthly && !hasYearly) {
          const monthlyOption = plan.billingOptions.find(
            (o) => o.name === "Monthly",
          );
          const yearlyPrice = Math.round(monthlyOption.price * 12 * 0.7);
          plan.billingOptions.push({
            id: monthlyOption.id,
            name: "Yearly",
            price: yearlyPrice,
            currency: monthlyOption.currency,
            discount: 30,
          });
        } else if (hasYearly && !hasMonthly) {
          const yearlyOption = plan.billingOptions.find(
            (o) => o.name === "Yearly",
          );
          const monthlyPrice =
            yearlyOption.price === 0
              ? 0
              : Math.round(yearlyOption.price / 12 / 0.7);
          plan.billingOptions.push({
            id: yearlyOption.id,
            name: "Monthly",
            price: monthlyPrice,
            currency: yearlyOption.currency,
            discount: 0,
          });
        }

        // Initialize billing cycle for this plan
        if (!this.planBillingCycles[plan.name]) {
          this.planBillingCycles = {
            ...this.planBillingCycles,
            [plan.name]: "Monthly",
          };
        }
      });

      return Object.values(groupedPlans);
    },

    getMonthlyPrice(plan) {
      if (!plan.billingOptions) return plan.price;
      const option = plan.billingOptions.find((o) => o.name === "Monthly");
      return option ? option.price : plan.price;
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    this.fetchSubscriptions();
    this.fetchUserSubscription();
  },
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
